import React from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import {
  Slide,
  Grid,
  Typography,
  Button,
  Dialog,
  IconButton,
  Box,
  NativeSelect,
} from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';
import LocaleKeys from '../../../locales/keys';
import { useState, useEffect, useContext } from 'react';
import { useUpdateSubscription } from '../../../common/hooks/useUpdateSubscription';
import postMessage from '../../../utils/postMessage';
import useSubscriptionFlowAnalyticsParams from '../../../common/hooks/useSubscriptionFlowAnalyticsParams';
import selectBackground from '../../../images/select-bg.png';
import { CancelSubscriptionInfo } from './quickCancelSubscriptionInfo';
import {
  QUICK_CANCEL_BACKED_OUT,
  QUICK_CANCEL_CONFIRMED,
} from '../../../constants/eventNames.constant';
import {
  quickCancelAnalyticEvents,
  quickCancelConfirmedAnalyticEvents,
} from '../../helpers/quickCancelHelper';
import useCodeTable from '../../../common/hooks/useCodeTable';
import { WebApiContext } from 'ascendon-web-api';

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
  dialogModal: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
  },
  fieldSelectNative: {
    height: '10%',
    marginBottom: '15px',
    '&.MuiInputBase-root': {
      border: '1px solid #ccc',
      borderRadius: '4px',
      '&.Mui-focused': {
        borderColor: '#66afe9',
        boxShadow:
          'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102, 175, 233, 0.6)',
      },
    },
    '& .MuiNativeSelect-select': {
      height: '50%',
      color: '#555',
      fontSize: '14px',
      background: `url(${selectBackground}) no-repeat right center;`,
    },
  },
  subscriptionValidUntilInfo: {
    fontSize: '18px',
    fontWeight: 500,
    marginBottom: '24px',
    lineHeight: '1.1',
  },
  cancellationReasonSubtitle: {
    fontSize: '18px',
    fontWeight: 500,
    marginBottom: '24px',
    lineHeight: '1.1',
  },
}));

const styles = (theme) => ({
  paper: {
    margin: '10px',
    maxWidth: '100%',
    position: 'fixed',
    top: 0,
    [theme.breakpoints.down('md')]: {
      maxWidth: '340px',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(0),
    color: theme.palette.custom.black,
    opacity: 0.2,
    paddingTop: '12px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  button: {
    color: theme.palette.custom.white,
    borderRadius: '5px',
    width: '240px',
    height: '37px',
    fontWeight: 500,
    letterSpacing: 0.5,
    padding: '12px 15px',
    lineHeight: 1,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  closeIcon: {
    fontSize: '21px',
    fontWeight: '700',
    lineHeight: 1,
    textShadow: `0 1px 0 ${theme.palette.custom.white}`,
    padding: '3px 0',
  },
  cancelButton: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      boxShadow: '0 0 0 2px #e00 inset',
      background: theme.palette.custom.white,
      color: theme.palette.custom.lightBlack,
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: '20px',
    },
  },
  backButton: {
    backgroundColor: theme.palette.custom.darkGreyBg,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.custom.white,
    },
  },
  dialogTitle: {
    lineHeight: 1.42,
    fontFamily: 'Formula1-Black',
  },
  dialogContainer: {
    backgroundColor: '#fcf8e3',
  },
  returnToSubscriptionButton: {
    backgroundColor: theme.palette.custom.darkGreyBg,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, onUpdate, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h2" className={classes.dialogTitle}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          data-testid="security-code-close-button"
          onClick={onClose}
          disableRipple
          disableFocusRipple
          variant="text"
          size="small"
        >
          <CloseRoundedIcon className={classes.closeIcon} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogModal = withStyles(styles)((props) => {
  const { children, classes, onClose, handleClose, open, ...other } = props;
  return (
    <Dialog
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-labelledby="subscription-dialog-title"
      classes={{ paper: classes.paper }}
      open={open}
      {...other}
    >
      {children}
    </Dialog>
  );
});

const DialogButtonAction = withStyles(styles)((props) => {
  const { children, classes, handleClose, name, ...other } = props;

  const buttonStyle =
    name === 'cancelButton'
      ? classes.cancelButton
      : name === 'doNotCancelButton' || 'returnToSubscriptionButton'
      ? classes.backButton
      : '';
  return (
    <Button
      autoFocus
      size="small"
      className={`${classes.button} ${buttonStyle}`}
      onClick={handleClose}
      {...other}
    >
      {children}
    </Button>
  );
});

const DialogContent = withStyles(() => ({
  root: {
    padding: '15px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles(() => ({
  root: {
    margin: 0,
    padding: '15px',
  },
}))(MuiDialogActions);

export function CancellationReasonForm({
  handleClose,
  open,
  subId,
  onUpdate,
  hasRetentionOffer,
  cycleDate,
  codeTable,
  handleCloseErrorDialog,
  currentPlan,
  imageUrl,
  statusName,
  planImageList,
  subscriptionList,
  subscriptionListClasses,
  isQuickCancel,
  retrieveEligibilityUiModel,
  allSubscriptions,
  onUpdateConfirmationPopupHandler,
}) {
  const defaultCancellationIdentifier =
    ASCENDON_ENV_CONFIG.retentionConfiguration?.defaultCancellationIdentifier;
  const defaultCancellationReason =
    ASCENDON_ENV_CONFIG.retentionConfiguration?.defaultCancellationReason;

  const { t } = useTranslation();
  const classes = useStyles();
  const [terminationReason, setTerminationReason] = useState('Select');
  const [filteredReasons, setFilteredReasons] = useState([]);
  const DEFAULT_LOCALE = 'en-GB';

  const [
    updateSubscription,
    updateSubscriptionFault,
    requestUpdateSubscription,
    updateSubscriptionFaultStatus,
  ] = useUpdateSubscription();
  const [staticCodeTable, , requestStaticCodeTable] = useCodeTable();

  const [language, ,] = useSubscriptionFlowAnalyticsParams();
  const WebApi = useContext(WebApiContext);
  /**
   * Request to send reasonForCancel in the quickCancelConfirmed analytic event as en-GB text, regardless of whether the user changes to another language.
   */
  const requestCodeTableWithUpdateSettings = () => {
    if (isQuickCancel && language !== DEFAULT_LOCALE) {
      // Set the webApi language to en-GB using the updateSettings method.
      WebApi.updateSettings({
        language: DEFAULT_LOCALE,
      });
      requestStaticCodeTable([150]);
      // Reset the webApi language to the user-selected language using the updateSettings method.
      WebApi.updateSettings({
        language,
      });
    }
  };

  useEffect(() => {
    postMessage('module.showBackdrop');
  }, []);

  const lookupTerminationReasonText = (terminationReason) => {
    const defaultTerminationReason = codeTable?.Codes?.find(
      (item) => item.Description === defaultCancellationIdentifier,
    )?.Name;
    if (terminationReason && codeTable) {
      const terminationReasonText = codeTable?.Codes?.find(
        (item) => item.Value === terminationReason,
      );
      return terminationReasonText?.Name || defaultTerminationReason;
    } else {
      return defaultTerminationReason;
    }
  };

  const postMessageAnalyticsQuickCancelConfirmed = (
    selectedTerminationReason,
    codeTable,
  ) => {
    quickCancelConfirmedAnalyticEvents(
      language,
      allSubscriptions,
      retrieveEligibilityUiModel?.EntitledPlans,
      isQuickCancel,
      QUICK_CANCEL_CONFIRMED,
      currentPlan,
      {
        terminationReasonCode: selectedTerminationReason,
        terminationReasonCodeTable: codeTable?.Codes,
      },
    );
  };

  const confirmCancellation = () => {
    if (terminationReason === 'Select' && codeTable) {
      const result = codeTable.Codes?.filter(
        (item) => item.Description === defaultCancellationIdentifier,
      );
      const selectedTerminationReason =
        result[0]?.Value || defaultCancellationReason;
      postMessage('module.showLoader');
      requestUpdateSubscription({
        Renew: false,
        TerminationReason: selectedTerminationReason,
        Id: subId,
      });
      postMessageAnalyticsQuickCancelConfirmed(
        selectedTerminationReason,
        staticCodeTable || codeTable,
      );
    } else {
      const selectedTerminationReason =
        terminationReason || defaultCancellationReason;
      postMessage('module.showLoader');
      requestUpdateSubscription({
        Renew: false,
        TerminationReason: selectedTerminationReason,
        Id: subId,
      });
      postMessageAnalyticsQuickCancelConfirmed(
        selectedTerminationReason,
        staticCodeTable || codeTable,
      );
    }
  };

  const sendManageSubscriptionMessage = (updateSubscription) => {
    if (updateSubscription) {
      const terminationReasonName =
        lookupTerminationReasonText(terminationReason);
      let splitPlan = currentPlan || ' ';
      const paymentType = 'Credit Card';
      splitPlan = splitPlan.split(' ');

      const updatedSubscriptionPackage =
        updateSubscription?.Subscription?.Items[0]?.Product?.Name;
      const event = 'manageSubscription';
      const cancelSubscription = {
        event,
        subscriptionPlan: splitPlan[0],
        paymentType: paymentType,
        subscriptionType: splitPlan[1],
        reasonForCancel: terminationReasonName,
        actionType: 'cancel',
        subscriptionPackage: updatedSubscriptionPackage,
        selectedLanguage: language,
      };
      postMessage(event, cancelSubscription);
    }
  };

  useEffect(() => {
    if (codeTable?.Codes) {
      setFilteredReasons(
        codeTable?.Codes?.filter(
          (item) => item.Description !== defaultCancellationIdentifier,
        ),
      );
      requestCodeTableWithUpdateSettings();
    }
  }, [codeTable]);

  useEffect(() => {
    if (updateSubscription) {
      postMessage('module.hideBackdrop');
      postMessage('module.hideLoader');
      handleClose(false);
      sendManageSubscriptionMessage(updateSubscription);
      if (onUpdate) {
        onUpdate(updateSubscription);
      }
      onUpdateConfirmationPopupHandler();
    }
  }, [updateSubscription]);

  useEffect(() => {
    if (
      updateSubscriptionFault &&
      typeof updateSubscriptionFaultStatus === 'boolean'
    ) {
      postMessage('module.hideLoader');
      postMessage('module.hideBackdrop');
      handleCloseErrorDialog(updateSubscriptionFault);
    }
  }, [updateSubscriptionFault, updateSubscriptionFaultStatus]);

  const cancelButtonClick = () => {
    postMessage('module.hideBackdrop');
    handleClose();
    quickCancelAnalyticEvents(
      language,
      allSubscriptions,
      retrieveEligibilityUiModel?.EntitledPlans,
      isQuickCancel,
      QUICK_CANCEL_BACKED_OUT,
      currentPlan,
    );
  };

  return (
    <div>
      <DialogModal
        handleClose={() => {
          postMessage('module.hideBackdrop');
          handleClose();
        }}
        className={classes.dialogModal}
        open={open}
      >
        <DialogTitle
          id="cancel-reason-form-title"
          style={{
            padding: '15px',
            textTransform: 'uppercase',
            alignItems: 'center',
            justifyContent: ' center',
          }}
          onClose={() => {
            postMessage('module.hideBackdrop');
            handleClose();
          }}
        >
          {t(LocaleKeys.retention.cancellationReasonTitle)}
        </DialogTitle>
        <DialogContent
          dividers
          style={{
            borderColor: '#e5e5e5',
          }}
        >
          <Grid container className={classes.root}>
            <Grid container spacing={6}>
              <Box component={Grid} item xs={12}>
                <CancelSubscriptionInfo
                  subscriptionListClasses={subscriptionListClasses}
                  imageUrl={imageUrl}
                  statusName={statusName}
                  planImageList={planImageList}
                  subscriptionList={subscriptionList}
                />
              </Box>
              <Box component={Grid} item xs={12}>
                {!hasRetentionOffer && (
                  <Typography className={classes.cancellationReasonSubtitle}>
                    {t(LocaleKeys.retention.cancellationReasonSubtitle)}
                  </Typography>
                )}
                <Box>
                  <>
                    <NativeSelect
                      IconComponent={() => null}
                      fullWidth
                      className={classes.fieldSelectNative}
                      disableUnderline
                      name="terminationReason"
                      value={terminationReason}
                      onChange={(event) => {
                        setTerminationReason(event.target.value);
                      }}
                    >
                      <option value="select">
                        {t(
                          LocaleKeys.retention
                            .cancellationFormSelectDefaultValue,
                        )}
                      </option>
                      {filteredReasons?.length ? (
                        filteredReasons?.map((cancelItem, key) => (
                          <option value={cancelItem.Value} key={key}>
                            {cancelItem.Name}
                          </option>
                        ))
                      ) : (
                        <option
                          value={defaultCancellationReason}
                          key={defaultCancellationIdentifier}
                        >
                          {defaultCancellationIdentifier}
                        </option>
                      )}
                    </NativeSelect>
                  </>
                </Box>
                <Typography className={classes.subscriptionValidUntilInfo}>
                  <Trans
                    i18nKey={
                      LocaleKeys.retention
                        .subscriptionItemCancelSubscriptionMessageFormat
                    }
                    components={{ bold: <strong /> }}
                    values={{
                      cycleDate,
                    }}
                  />
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Grid container justifyContent="space-between">
            <DialogButtonAction
              disableRipple
              disableFocusRipple
              size="small"
              name="cancelButton"
              handleClose={confirmCancellation}
            >
              {t(LocaleKeys.retention.cancellationFormSave)}
            </DialogButtonAction>
            <DialogButtonAction
              disableRipple
              disableFocusRipple
              size="small"
              name="doNotCancelButton"
              handleClose={cancelButtonClick}
            >
              {t(LocaleKeys.retention.cancellationFormCancel)}
            </DialogButtonAction>
          </Grid>
        </DialogActions>
      </DialogModal>
    </div>
  );
}

export default CancellationReasonForm;
